/**
 * 事件时间轴数据 Hook
 * 用于获取和处理 /overview/event_stats_timeline 接口数据
 */
import { useState, useCallback, useEffect } from 'react';
import {
  getEventStatsTimeline,
  IEventStatsTimelineResponse,
} from '@op/services';
import { useCancelRequestHook } from '@op/hooks/useCancelRequest';
import { getRealXAxisValue } from '@op/utils/chart';
import { ParamsTimeType } from '@op/types/common';
import moment from 'moment';

// ========== 类型定义 ==========

export type EventDataType = {
  eventList: any; // 选择器渲染的数据
  chartRenderEventList: any; // 渲染图表event的数据
  format: string; // 格式化的时间格式
  isEmpty: boolean;
  bubbleEvent: { [key: string]: Array<any> }; // EventWindows 组件需要的扁平化事件数据
  getEventSeries?: TGetEventSeries; // 获取事件定位用的 dummy series
  legendSelected: { [key: string]: boolean }; // legend 选中状态配置
  chartShowEventTopSpace: { [key in ParamsTimeType]: number }; // 事件气泡顶部空间配置
  hasEventBubbles: boolean; // 是否有事件气泡
};

interface IUseEventDataOptions {
  autoFetch?: boolean;
  chartShowEventTopSpace?: Partial<{ [key in ParamsTimeType]: number }>;
}

type FetchDataFunc = (filter?: any) => Promise<void>;

// 获取事件系列函数类型
export type TGetEventSeries = (
  chartType: 'line' | 'bar',
  xAxisLength: number,
) => any;

export interface IUseEventDataResult {
  eventData: EventDataType;
  loading: boolean;
  error: boolean;
  fetchData: FetchDataFunc;
}

// ========== 数据处理函数 ==========

/**
 * 创建事件定位用的 dummy series
 * 用于在图表中添加一个不可见的系列，用于事件定位
 */
export const createEventSeries = (
  hasEventBubbles: boolean,
  chartType: 'line' | 'bar',
  xAxisLength: number,
): any => {
  const totalList = new Array(xAxisLength).fill(0);
  if (hasEventBubbles) {
    return {
      name: 'TopTipsEvent',
      type: chartType,
      stack: chartType === 'bar' ? 'total' : undefined,
      data: totalList,
    } as any;
  }
  return null;
};

/**
 * 处理周数据，将多个单日数据并入周内
 */
const getChartRenderEventList = (
  chartRenderEventList: any,
  filter: any,
  data: any,
) => {
  if (filter?.date_type === 'weekly') {
    const resultCommon: any = {};
    Object.keys(chartRenderEventList || {}).forEach(key => {
      const item = chartRenderEventList[key];
      const realKey = getRealXAxisValue({
        xAxis: key,
        dateType: filter.date_type,
        xAxisData: data?.xAxisData,
        format: data?.format,
      });
      if (!resultCommon[realKey]) {
        resultCommon[realKey] = [];
      }

      resultCommon[realKey].push(item);
    });

    const result: {
      [key: string]: {
        count: number;
        event_list: { [key: string]: Array<any> };
      };
    } = {};
    Object.keys(resultCommon).forEach((key: string) => {
      const elements = resultCommon[key];
      elements.forEach((item: any) => {
        const { count, event_list } = item || {};
        if (!result[key]) {
          result[key] = { count: 0, event_list: {} };
        }
        result[key].count = result[key].count + (count || 0);

        Object.keys(event_list || {}).forEach(eventKey => {
          if (!result[key].event_list[eventKey]) {
            result[key].event_list[eventKey] = [];
          }

          result[key].event_list[eventKey].push(
            ...(event_list[eventKey] || []),
          );
        });
      });
    });
    return result;
  }

  return chartRenderEventList;
};

/**
 * 处理事件列表数据，转换为组件所需格式
 */
function dealwithEventList(
  data: IEventStatsTimelineResponse,
  chartData: { xAxisData: Array<any>; format: string },
  filter: any,
  chartShowEventTopSpace: { [key in ParamsTimeType]: number },
): EventDataType {
  const chartRenderEventList: any = {};

  const eventTimeline = data.event_timeline || {};
  if (Object.keys(eventTimeline).length === 0) {
    const emptyEventData: EventDataType = {
      eventList: data.event_list || {},
      chartRenderEventList: {},
      format: chartData.format,
      isEmpty: true,
      bubbleEvent: {},
      legendSelected: {},
      getEventSeries: undefined,
      chartShowEventTopSpace,
      hasEventBubbles: false,
    };
    return emptyEventData;
  }

  // 使用 Object.entries 遍历并转换时间格式
  Object.entries(eventTimeline).forEach(([timestamp, element]) => {
    const formattedKey = moment(timestamp).format(chartData.format);
    chartRenderEventList[formattedKey] = element;
  });

  const processedChartRenderEventList = getChartRenderEventList(
    chartRenderEventList,
    filter, // 默认使用 day
    chartData,
  );

  // 生成 EventWindows 组件需要的扁平化事件数据
  const bubbleEvent: { [key: string]: Array<any> } = {};
  Object.keys(processedChartRenderEventList || {}).forEach(timestamp => {
    const timelineItem = processedChartRenderEventList[timestamp];
    if (!timelineItem.event_list) return;

    const xAxisValue = timestamp; // 已经是格式化后的 key
    const eventList: Array<any> = [];
    Object.keys(timelineItem.event_list).forEach(eventType => {
      const events = timelineItem.event_list[eventType];
      events.forEach((event: any) => {
        eventList.push({
          ...event,
          event_type: eventType,
          event_type_cn: eventType,
        });
      });
    });

    if (eventList.length > 0) {
      bubbleEvent[xAxisValue] = eventList;
    }
  });

  const hasEventBubbles = Object.keys(bubbleEvent).length > 0;

  const baseEventData: EventDataType = {
    eventList: data.event_list || {}, // 选择器渲染的数据
    chartRenderEventList: processedChartRenderEventList, // 渲染图表event的数据
    format: chartData.format, // 格式化的时间格式
    isEmpty: Object.keys(chartRenderEventList).length === 0,
    bubbleEvent, // EventWindows 组件需要的扁平化事件数据
    getEventSeries: (chartType: 'line' | 'bar', xAxisLength: number) =>
      createEventSeries(hasEventBubbles, chartType, xAxisLength),
    legendSelected: { TopTipsEvent: false },
    chartShowEventTopSpace,
    hasEventBubbles,
  };

  return baseEventData;
}

// 默认的顶部空间配置
const defaultChartShowEventTopSpace: { [key in ParamsTimeType]: number } = {
  daily: 60,
  weekly: 60,
  monthly: 130,
  hourly: 50,
};

// ========== Hook ==========

export const useEventData = (
  filter?: any,
  chartData?: { xAxisData?: Array<any>; format?: string },
  options?: IUseEventDataOptions,
): IUseEventDataResult => {
  const { autoFetch = true, chartShowEventTopSpace: customTopSpace } =
    options || {};

  // 合并自定义配置
  const chartShowEventTopSpace = {
    ...defaultChartShowEventTopSpace,
    ...customTopSpace,
  } as any;

  const [eventData, setEventData] = useState<EventDataType>({
    eventList: {},
    chartRenderEventList: {},
    format: '',
    isEmpty: true,
    bubbleEvent: {},
    getEventSeries: undefined,
    legendSelected: {},
    chartShowEventTopSpace,
    hasEventBubbles: false,
  });

  const { wrapperCancelRequest, error, loading } =
    useCancelRequestHook<IEventStatsTimelineResponse>({ defaultLoading: true });

  const fetchData = useCallback(async (filter: any) => {
    if (!filter?.start_time || !filter?.end_time) {
      return;
    }

    try {
      const res = await wrapperCancelRequest(getEventStatsTimeline, filter);

      const defaultChartData = {
        xAxisData: chartData?.xAxisData || [],
        format: chartData?.format || 'YYYY-MM-DD',
      };

      // 使用模拟数据进行测试
      const chartEventList = dealwithEventList(
        res || { event_list: {}, event_timeline: {} },
        defaultChartData,
        filter,
        chartShowEventTopSpace,
      );

      setEventData(chartEventList);
    } catch (err) {
      console.error('获取事件时间轴数据失败:', err);
    }
  }, []);

  useEffect(() => {
    if (!filter || !autoFetch) return;
    fetchData(filter);
  }, [filter, autoFetch, fetchData]);

  return {
    eventData,
    loading,
    error,
    fetchData,
  };
};

export default useEventData;
